import './BargainWarehouse.scss';
import {
  PageTitle,
  LoadingCircle,
  ErrorDisplayComponent,
  ErrorBoundary,
  Breadcrumbs,
  TabbedContent,
  ContentTable,
  type TabConfig,
} from '@gplibraries/react/components';
import { bargainWarehouseTableConfig } from "../../config/bargainWarehouseTableConfig.ts";
import { bargainWarehousePartsTableConfig } from "../../config/bargainWarehousePartsTableConfig.ts";
import { useTokenProvider, useConfig, useLogger } from '@gplibraries/react/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { fetchBargainWarehouseProducts, fetchBargainWarehouseParts } from '../../api/bargainWarehouse.js';

export default function BargainWarehouse() {

  const logger = useLogger();
  const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
  const getToken = useTokenProvider({ scopes: [dealerAccessApiScope]});
  const [params, setParams] = useSearchParams();
  const [bwCategory, setBwCategory] = useState<string>(params.get('category') || 'all');

  const { isLoading: productsLoading, error: productsError, data: products } = useQuery({
    enabled: Boolean(getToken),
    queryKey: ['bargain_warehouse'],
    queryFn: () => getToken!()
      .then(token => fetchBargainWarehouseProducts(dealerAccessApiHost, token!.accessToken))
      .then(res => res.json()),
    select: (data) => data.data // only want to work with the data property of the response
  })

  const { isLoading: partsLoading, error: partsError, data: parts } = useQuery({
    enabled: Boolean(getToken),
    queryKey: ['bargain_warehouse', 'parts'],
    queryFn: () => getToken!()
      .then(token => fetchBargainWarehouseParts(dealerAccessApiHost, token!.accessToken))
      .then(res => res.json()),
    select: (data) => data.data // only want to work with the data property of the response
  })

  // Update URL query parameter when category changes
  useEffect(() => {
    setParams({ category: bwCategory })
  }, [bwCategory]);

  if (productsError) logger.error(productsError, { source: 'BargainWarehouse' });
  if (partsError) logger.error(partsError, { source: 'BargainWarehouse' })

  const loading = partsLoading || productsLoading;

  return (
    <div className={'flex-col full'}>

      <Breadcrumbs/>

      <PageTitle>Bargain Warehouse</PageTitle>

      <section className={'flex-col full bargain-warehouse-container'}>
        <ErrorBoundary
          fallback={<ErrorDisplayComponent message={'Bargain Warehouse could not be loaded.'}/>}
          logger={logger}
        >

        {loading && <LoadingCircle className={'large color'} />}

        {(products?.length > 0 || parts?.length > 0) &&
          <TabbedContent
            onTabChange={(tabId: string) => setBwCategory(tabId)}
            activeTab={bwCategory}
            tabs={[
              {
                id: 'all',
                label: 'All'
              },
              // Get unique list of categories and render a tab for each
              ...[...new Set(products?.map((p: any) => p.field_unit_category.name))]
              .map((categoryName: any): TabConfig => {
                const category = products.find((p: any) => p.field_unit_category.name === categoryName).field_unit_category;
                return {
                  id: category.tid,
                  label: categoryName
                }
              }),
              {
                id: 'parts',
                label: 'Parts'
              }
            ]}
          >

            <div className="listings-table-wrapper">

              {(bwCategory === 'all' && products?.length === 0) &&
                <div className={'flex-row center no-content-message'}>
                  Sorry, no Bargain Warehouse wholegood listings are available at this time.
                </div>
              }

              {(bwCategory !== 'parts' && products?.length > 0) &&
                <ContentTable
                  content={products
                    .filter((p: any) => bwCategory === 'all' ? true : p.field_unit_category.tid == bwCategory)
                    .sort((a: any, b: any) => a.title.localeCompare(b.title))
                    .map((product: any) => ({
                      model_number: {
                        text: product.title,
                        url: `/bargain-warehouse/${product.drupal_internal__nid}`
                      },
                      serial_number: product.field_serial_number,
                      discount: product.field_discount_available,
                      msrp: product.field_list_price,
                      dealer_net: product.field_dealer_net,
                      qty: product.field_quantity,
                      starting_bid: product.field_starting_bid,
                      reserve: product.field_is_reserve_met === false ? null : product.field_is_reserve_met,
                      closing_date: product.field_closing_date
                    }))
                  }
                  tableConfig={bargainWarehouseTableConfig}
                  paginated={true}
                />
              }

              {(bwCategory === 'parts' && parts?.length > 0) &&
                <ContentTable
                  content={parts.map((part: any) => ({
                    part: {
                      text: part.title,
                      url: `/bargain-warehouse/parts/${part.drupal_internal__nid}`
                    },
                    part_number: part.field_part_number,
                    quantity: part.field_quantity,
                    list_price: part.field_list_price,
                    where_used: part.field_where_used,
                  }))}
                  tableConfig={bargainWarehousePartsTableConfig}
                  paginated={true}
                />
              }

            </div>

          </TabbedContent>
        }

        </ErrorBoundary>
      </section>


    </div>
  );
}